import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import Header from '../../components/Header'
import { useTheme } from '../../contexts/ThemeContext'
import { useAuth } from '../../contexts/AuthContext'
import { coursesAPI, getApiBaseUrlWithoutApi } from '../../config/api'
import ExpiredSubscriptionModal from '../../components/ExpiredSubscriptionModal'
import { motion } from 'framer-motion'
import 'primeicons/primeicons.css'

export default function MyCoursesPage() {
  const { theme } = useTheme()
  const { isAuthenticated } = useAuth()
  const navigate = useNavigate()
  const [isChecking, setIsChecking] = useState(true)
  const [enrolledCourses, setEnrolledCourses] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [showExpiredModal, setShowExpiredModal] = useState(false)

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsChecking(false)
      if (!isAuthenticated) {
        navigate('/login')
      }
    }, 100)

    return () => clearTimeout(timer)
  }, [isAuthenticated, navigate])

  useEffect(() => {
    const fetchMyCourses = async () => {
      if (!isAuthenticated || isChecking) return
      
      try {
        setLoading(true)
        const data = await coursesAPI.getMyCourses()
        const API_BASE_URL = getApiBaseUrlWithoutApi()
        const transformed = Array.isArray(data) ? data.map((course: any) => {
          let imageUrl = course.image || '/images/team-photo-1-BB_Ia2hP.jpg'
          if (imageUrl && imageUrl.startsWith('/media/')) {
            imageUrl = `${API_BASE_URL}${imageUrl}`
          } else if (imageUrl && !imageUrl.startsWith('http') && !imageUrl.startsWith('/images/')) {
            imageUrl = `${API_BASE_URL}${imageUrl}`
          }
          
          return {
            id: course.id,
            title: course.title || course.name,
            subtitle: course.subtitle,
            description: course.description,
            image: imageUrl,
            progress: course.enrollment?.progress || 0,
            videoCount: course.video_count || course.lessons_count || 0,
            completedVideos: course.enrollment?.completed_videos || 0,
            duration: course.duration || '30',
            status: course.enrollment?.status || 'in-progress',
            enrolledDate: course.enrollment?.enrolled_date || course.enrollment?.created_at,
            lastAccessed: course.enrollment?.last_accessed || course.updated_at
          }
        }) : []
        setEnrolledCourses(transformed)
      } catch (error: any) {
        // Error fetching my courses
        
        if (error.response?.status === 403) {
          const errorMessage = error.response?.data?.error || ''
          if (errorMessage.includes('Kỳ hạn') || errorMessage.includes('hết hạn')) {
            const dismissedKey = `expired_subscription_modal_dismissed_/course/my-courses`
            const wasDismissed = localStorage.getItem(dismissedKey)
            
            if (!wasDismissed) {
              setShowExpiredModal(true)
            }
          }
        }
        
        setEnrolledCourses([])
      } finally {
        setLoading(false)
      }
    }

    fetchMyCourses()
  }, [isAuthenticated, isChecking])

  if (isChecking) {
    return (
      <main className={`min-h-screen flex items-center justify-center ${theme === 'dark' ? 'bg-black' : 'bg-gray-100'}`}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gold-400 mx-auto mb-4"></div>
          <p className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>Đang tải...</p>
        </div>
      </main>
    )
  }

  if (!isAuthenticated) {
    return null
  }

  return (
    <main className={`min-h-screen ${theme === 'dark' ? 'bg-black' : 'bg-gray-100'}`}>
      <Header />
      
      <ExpiredSubscriptionModal
        isOpen={showExpiredModal}
        onClose={() => setShowExpiredModal(false)}
      />

      {/* Header Section */}
      <section className="relative py-20 md:py-28 px-4 overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0 z-0">
          <img
            src="/images/homepage-background.png"
            alt="Background"
            className="w-full h-full object-cover"
            onError={(e) => {
              // Fallback to team photo if homepage background fails
              e.currentTarget.src = '/images/team-photo-1-BB_Ia2hP.jpg'
            }}
          />
          {/* Dark Overlay for better text readability */}
          <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/70 to-black/60"></div>
          {/* Gold gradient overlay for accent */}
          <div className="absolute inset-0 bg-gradient-to-r from-gold-900/20 via-transparent to-gold-900/20"></div>
        </div>
        
        {/* Content */}
        <div className="relative z-10 container mx-auto text-center max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-4xl md:text-5xl lg:text-6xl font-extrabold mb-6 drop-shadow-2xl"
            >
              <span className="bg-gradient-to-r from-gold-300 via-gold-400 to-gold-300 bg-clip-text text-transparent">
                Khóa học của tôi
              </span>
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-lg md:text-xl lg:text-2xl text-gray-100 font-medium drop-shadow-lg"
            >
              Tiếp tục học tập và phát triển kỹ năng bóng đá của tôi
            </motion.p>
          </motion.div>
          
          {/* Decorative Elements */}
          <div className="absolute top-0 left-0 w-full h-full pointer-events-none">
            <div className="absolute top-10 left-10 w-20 h-20 border-2 border-gold-400/30 rounded-full opacity-50"></div>
            <div className="absolute bottom-10 right-10 w-32 h-32 border-2 border-gold-400/20 rounded-full opacity-30"></div>
            <div className="absolute top-1/2 left-1/4 w-16 h-16 border border-gold-500/20 rounded-full opacity-40"></div>
          </div>
        </div>
      </section>

      {/* Courses Section */}
      <section className={`py-12 md:py-16 px-4 ${theme === 'dark' ? 'bg-black' : 'bg-gray-100'}`}>
        <div className="container mx-auto max-w-7xl">
          {loading ? (
            <div className="text-center py-16">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gold-400 mx-auto mb-4"></div>
              <p className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>Đang tải khóa học...</p>
            </div>
          ) : enrolledCourses.length > 0 ? (
            <>
              {/* Stats Summary */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className={`mb-8 p-6 rounded-2xl ${
                  theme === 'dark' 
                    ? 'bg-gradient-to-r from-gold-900/20 via-gold-800/10 to-transparent border border-gold-500/20' 
                    : 'bg-gradient-to-r from-gold-50 to-white border border-gold-200'
                }`}
              >
                <div className="flex flex-wrap items-center justify-between gap-4">
                  <div>
                    <h3 className={`text-lg font-semibold mb-1 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                      Tổng số khóa học
                    </h3>
                    <p className={`text-3xl font-bold ${theme === 'dark' ? 'text-gold-400' : 'text-gold-600'}`}>
                      {enrolledCourses.length}
                    </p>
                  </div>
                  <div className="flex gap-6">
                    <div className="text-center">
                      <p className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'} mb-1`}>Đang học</p>
                      <p className={`text-xl font-bold ${theme === 'dark' ? 'text-cyan-400' : 'text-cyan-600'}`}>
                        {enrolledCourses.filter(c => c.status === 'in-progress').length}
                      </p>
                    </div>
                    <div className="text-center">
                      <p className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'} mb-1`}>Hoàn thành</p>
                      <p className={`text-xl font-bold ${theme === 'dark' ? 'text-green-400' : 'text-green-600'}`}>
                        {enrolledCourses.filter(c => c.status === 'completed').length}
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* Courses Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {enrolledCourses.map((course, index) => (
                  <motion.div
                    key={course.id}
                    initial={{ opacity: 0, y: 30, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    transition={{ duration: 0.4, delay: index * 0.05 }}
                    whileHover={{ y: -8, scale: 1.02 }}
                    className={`group relative rounded-2xl overflow-hidden ${
                      theme === 'dark' 
                        ? 'bg-gradient-to-br from-gray-900/90 to-black border border-gold-500/20' 
                        : 'bg-white border border-gold-200/50'
                    } shadow-lg ${theme === 'dark' ? 'shadow-gold-500/5' : 'shadow-gray-200'} hover:shadow-2xl ${theme === 'dark' ? 'hover:shadow-gold-500/20' : 'hover:shadow-gold-300/30'} transition-all duration-300 cursor-pointer`}
                    onClick={() => navigate(`/course/online/${course.id}`)}
                  >
                    {/* Course Image */}
                    <div className="relative aspect-[16/9] overflow-hidden">
                      <img
                        src={course.image}
                        alt={course.title}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                      {/* Gradient Overlay */}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent"></div>
                      
                      {/* Status Badge */}
                      <div className="absolute top-3 right-3">
                        {course.status === 'in-progress' && (
                          <div className="bg-cyan-500/95 backdrop-blur-sm text-white px-3 py-1.5 rounded-full flex items-center gap-1.5 shadow-lg">
                            <i className="pi pi-spin pi-spinner text-xs"></i>
                            <span className="text-xs font-semibold">Đang học</span>
                          </div>
                        )}
                        {course.status === 'completed' && (
                          <div className="bg-green-500/95 backdrop-blur-sm text-white px-3 py-1.5 rounded-full flex items-center gap-1.5 shadow-lg">
                            <i className="pi pi-check-circle text-xs"></i>
                            <span className="text-xs font-semibold">Hoàn thành</span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Course Content */}
                    <div className={`p-5 ${theme === 'dark' ? 'bg-gray-900/50' : 'bg-white'}`}>
                      {/* Title */}
                      <h3 className={`text-xl font-bold mb-2 line-clamp-2 ${theme === 'dark' ? 'text-white' : 'text-gray-900'} group-hover:text-gold-400 transition-colors`}>
                        {course.title}
                      </h3>

                      {/* Description */}
                      <p className={`text-sm leading-relaxed mb-4 line-clamp-2 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                        {course.description || course.subtitle || 'Khóa học bóng đá chuyên nghiệp'}
                      </p>

                      {/* Metrics */}
                      <div className="flex items-center gap-4 mb-4 pb-4 border-b border-gold-500/10">
                        <div className="flex items-center gap-2">
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${theme === 'dark' ? 'bg-gold-500/20' : 'bg-gold-100'}`}>
                            <i className={`pi pi-video text-gold-400 text-sm`}></i>
                          </div>
                          <div>
                            <p className={`text-xs ${theme === 'dark' ? 'text-gray-500' : 'text-gray-500'}`}>Video</p>
                            <p className={`text-sm font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                              {course.videoCount}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${theme === 'dark' ? 'bg-gold-500/20' : 'bg-gold-100'}`}>
                            <i className={`pi pi-clock text-gold-400 text-sm`}></i>
                          </div>
                          <div>
                            <p className={`text-xs ${theme === 'dark' ? 'text-gray-500' : 'text-gray-500'}`}>Thời gian</p>
                            <p className={`text-sm font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                              {course.duration} phút
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Action Button */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          navigate(`/course/online/${course.id}`)
                        }}
                        className={`w-full px-4 py-3 rounded-xl font-semibold text-sm transition-all duration-300 flex items-center justify-center gap-2 ${
                          course.status === 'in-progress'
                            ? 'bg-gold-gradient text-black hover:shadow-lg hover:shadow-gold-500/50 transform hover:scale-105'
                            : course.status === 'completed'
                            ? `${theme === 'dark' ? 'border-2 border-gold-400 text-gold-400 hover:bg-gold-400/10' : 'border-2 border-gold-500 text-gold-600 hover:bg-gold-50'}`
                            : `${theme === 'dark' ? 'bg-gray-800 text-gray-500 cursor-not-allowed' : 'bg-gray-200 text-gray-500 cursor-not-allowed'}`
                        }`}
                      >
                        {course.status === 'in-progress' ? (
                          <>
                            <i className="pi pi-play"></i>
                            <span>Tiếp tục học</span>
                          </>
                        ) : course.status === 'completed' ? (
                          <>
                            <i className="pi pi-refresh"></i>
                            <span>Xem lại</span>
                          </>
                        ) : (
                          <>
                            <i className="pi pi-clock"></i>
                            <span>Sắp ra mắt</span>
                          </>
                        )}
                      </button>
                    </div>

                    {/* Hover Effect Glow */}
                    <div className={`absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none ${
                      theme === 'dark' ? 'bg-gradient-to-br from-gold-500/5 to-transparent' : 'bg-gradient-to-br from-gold-50/50 to-transparent'
                    }`}></div>
                  </motion.div>
                ))}
              </div>
            </>
          ) : (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className={`text-center py-20 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}
            >
              <div className={`w-24 h-24 mx-auto mb-6 rounded-full flex items-center justify-center ${theme === 'dark' ? 'bg-gold-500/10' : 'bg-gold-100'}`}>
                <i className="pi pi-book text-5xl text-gold-400"></i>
              </div>
              <h3 className={`text-2xl font-bold mb-2 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                Chưa có khóa học nào
              </h3>
              <p className="text-lg mb-6">Bạn chưa đăng ký khóa học nào</p>
              <button
                onClick={() => navigate('/course/online')}
                className="px-8 py-3 bg-gold-gradient text-black font-bold rounded-lg hover:shadow-lg hover:shadow-gold-500/50 transition transform hover:scale-105"
              >
                Khám phá khóa học
              </button>
            </motion.div>
          )}
        </div>
      </section>
    </main>
  )
}
